"use client";

import { useState } from "react";
import Icon from "@/components/ui/AppIcon";

interface OrderSummaryProps {
  planId?: string;
  isUpgrade?: boolean;
  currentPlan?: string;
}

const planData = {
  "free": {
    name: "Digital Preview",
    tagline: "Try Before You Tag",
    oneTimePrice: null,
    monthlyPrice: 0,
    features: [
      "5 items (digital only)",
      "All 5 viewer templates",
      "Interactive markers",
      "Public profile page",
      "Cloud storage (500 MB)",
    ],
    badge: "Free",
    badgeColor: "bg-accent-emerald",
  },
  "starter": {
    name: "Starter Plan",
    tagline: "For Growing Collections",
    oneTimePrice: null,
    monthlyPrice: 9,
    features: [
      "25 active NFC tags",
      "Unlimited items",
      "All viewer templates",
      "Interactive markers",
      "Cloud storage",
    ],
    badge: "Selected",
    badgeColor: "bg-accent-crimson",
  },
  "starter-kit": {
    name: "Starter Kit",
    tagline: "Get Started with Hardware",
    oneTimePrice: 49,
    monthlyPrice: 9,
    features: [
      "5 premium NFC tags (hardware)",
      "First month included",
      "25 active tags max",
      "All viewer templates",
      "Interactive markers",
    ],
    badge: "Selected",
    badgeColor: "bg-accent-crimson",
  },
  "pro": {
    name: "Pro Plan",
    tagline: "For Serious Collectors",
    oneTimePrice: null,
    monthlyPrice: 19,
    features: [
      "100 active NFC tags",
      "Unlimited items",
      "Remove branding",
      "Priority support",
      "API access",
    ],
    badge: "Pro",
    badgeColor: "bg-accent-gold/20 text-accent-gold",
  },
  "collector": {
    name: "Collector Edition",
    tagline: "Premium Hardware Bundle",
    oneTimePrice: 49,
    monthlyPrice: 19,
    features: [
      "10 premium NFC tags (hardware)",
      "First month Pro included",
      "100 active NFC tags",
      "Unlimited items",
      "Priority support",
    ],
    badge: "Selected",
    badgeColor: "bg-accent-purple",
  },
};

export default function OrderSummary({ planId = "starter-kit", isUpgrade = false, currentPlan }: OrderSummaryProps) {
  const [promoOpen, setPromoOpen] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoError, setPromoError] = useState("");

  const plan = planData[planId as keyof typeof planData] || planData["starter-kit"];
  const isStarterKit = planId === "starter-kit";
  const isFree = planId === "free";

  const handlePromoApply = () => {
    if (promoCode.toUpperCase() === "TAPTALE10") {
      setPromoApplied(true);
      setPromoError("");
    } else {
      setPromoError("Invalid promo code. Try TAPTALE10");
      setPromoApplied(false);
    }
  };

  // Use oneTimePrice if available, otherwise monthlyPrice
  const subtotal = plan.oneTimePrice || plan.monthlyPrice;
  const discount = promoApplied ? Math.round(subtotal * 0.1) : 0;
  const total = subtotal - discount;

  return (
    <div className="sticky top-24">
      <div className="bg-bg-card border border-white/8 rounded-3xl overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-white/5">
          <h2 className="font-serif text-xl text-white">Order Summary</h2>
        </div>

        {/* Plan Card */}
        <div className="p-6">
          {/* Upgrade Badge */}
          {isUpgrade && currentPlan && (
            <div className="bg-accent-emerald/5 border border-accent-emerald/15 rounded-xl px-4 py-3 mb-4">
              <div className="flex items-center gap-2">
                <Icon name="ArrowUpIcon" size={14} className="text-accent-emerald" />
                <div>
                  <p className="text-accent-emerald text-xs font-semibold">Account Upgrade</p>
                  <p className="text-gray-500 text-[10px] mt-0.5">
                    From {currentPlan === 'free' ? 'Free Plan' : currentPlan}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-bg-elevated rounded-2xl p-5 mb-6 border border-white/5 relative overflow-hidden">
            {/* Decorative gradient */}
            <div className="absolute top-0 right-0 w-24 h-24 bg-accent-crimson/5 rounded-full blur-2xl" />

            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`text-[9px] font-bold uppercase tracking-widest px-2.5 py-0.5 rounded-full ${
                    isStarterKit ? "bg-accent-crimson text-white" : "bg-accent-gold/20 text-accent-gold"
                  }`}>
                    {plan.badge}
                  </span>
                </div>
                <h3 className="font-serif text-lg text-white">{plan.name}</h3>
                <p className="text-gray-500 text-xs mt-0.5">{plan.tagline}</p>
              </div>
              {/* NFC Icon */}
              <div className="w-12 h-12 rounded-xl bg-accent-crimson/10 border border-accent-crimson/20 flex items-center justify-center flex-shrink-0">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="text-accent-crimson">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                  <path d="M8.5 12c0-1.93 1.57-3.5 3.5-3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M6 12c0-3.31 2.69-6 6-6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  <circle cx="12" cy="12" r="1.5" fill="currentColor"/>
                  <path d="M15.5 12c0 1.93-1.57 3.5-3.5 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                  <path d="M18 12c0 3.31-2.69 6-6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
            </div>

            {/* Features */}
            <ul className="space-y-2">
              {plan.features.map((feat) => (
                <li key={feat} className="flex items-center gap-2.5 text-xs text-gray-400">
                  <Icon name="CheckCircleIcon" size={13} className="text-accent-emerald flex-shrink-0" />
                  {feat}
                </li>
              ))}
            </ul>
          </div>

          {/* Promo Code - hidden for free plan */}
          {!isFree && (
            <div className="mb-6">
              <button
                onClick={() => setPromoOpen(!promoOpen)}
                className="flex items-center gap-2 text-xs text-gray-500 hover:text-gray-300 transition-colors"
              >
                <Icon name="TagIcon" size={13} />
                <span>{promoApplied ? "Promo applied ✓" : "Have a promo code?"}</span>
                <Icon name={promoOpen ? "ChevronUpIcon" : "ChevronDownIcon"} size={12} />
              </button>

              {promoOpen && !promoApplied && (
                <div className="mt-3 flex gap-2">
                  <input
                    type="text"
                    value={promoCode}
                    onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                    placeholder="PROMO CODE"
                    className="flex-1 bg-bg-elevated border border-white/8 rounded-xl px-3 py-2 text-xs text-white placeholder-gray-600 font-mono focus:outline-none focus:border-accent-crimson/40 focus:shadow-[0_0_0_3px_rgba(255,0,76,0.08)] transition-all"
                  />
                  <button
                    onClick={handlePromoApply}
                    className="px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-xs text-white transition-all"
                  >
                    Apply
                  </button>
                </div>
              )}
              {promoError && (
                <p className="text-red-400 text-xs mt-1.5">{promoError}</p>
              )}
            </div>
          )}

          {/* Price Breakdown */}
          {!isFree && (
            <div className="space-y-3 mb-6">
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Subtotal</span>
                <span className="text-white">${subtotal.toFixed(2)}</span>
              </div>
              {promoApplied && (
                <div className="flex justify-between text-sm">
                  <span className="text-accent-emerald flex items-center gap-1">
                    <Icon name="TagIcon" size={12} /> Promo (10% off)
                  </span>
                  <span className="text-accent-emerald">-${discount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-gray-500">Tax</span>
                <span className="text-gray-400">Calculated at checkout</span>
              </div>
              {plan.oneTimePrice && plan.monthlyPrice && (
                <div className="flex justify-between text-xs text-gray-600">
                  <span>Then monthly</span>
                  <span>${plan.monthlyPrice}/mo after</span>
                </div>
              )}
            </div>
          )}

          {/* Total */}
          <div className="flex justify-between items-center py-4 border-t border-b border-white/5 mb-6">
            <span className="font-semibold text-white">
              {isFree ? "Plan" : "Total Today"}
            </span>
            <div className="text-right">
              <span className="font-serif text-2xl text-white">
                {isFree ? "FREE" : `$${total.toFixed(2)}`}
              </span>
              {isFree ? (
                <p className="text-accent-emerald text-[10px] mt-0.5">No payment required</p>
              ) : isStarterKit ? (
                <p className="text-gray-600 text-[10px] mt-0.5">one-time payment</p>
              ) : null}
            </div>
          </div>

          {/* Payment Methods */}
          <div className="mb-6">
            <p className="text-gray-600 text-[10px] uppercase tracking-widest font-mono mb-3">Accepted Payments</p>
            <div className="flex items-center gap-2 flex-wrap">
              {/* Visa */}
              <div className="bg-white rounded-md px-2 py-1 flex items-center">
                <span className="text-[#1a1f71] font-bold text-xs tracking-tight">VISA</span>
              </div>
              {/* Mastercard */}
              <div className="bg-white rounded-md px-2 py-1 flex items-center gap-1">
                <div className="w-4 h-4 rounded-full bg-[#eb001b]" />
                <div className="w-4 h-4 rounded-full bg-[#f79e1b] -ml-2" />
              </div>
              {/* BLIK */}
              <div className="bg-[#e2001a] rounded-md px-2 py-1">
                <span className="text-white font-bold text-[10px] tracking-tight">BLIK</span>
              </div>
              {/* Transfer */}
              <div className="bg-bg-elevated border border-white/10 rounded-md px-2 py-1">
                <span className="text-gray-400 text-[10px] font-mono">Transfer</span>
              </div>
              {/* Apple Pay */}
              <div className="bg-black border border-white/10 rounded-md px-2 py-1">
                <span className="text-white text-[10px] font-semibold"> Pay</span>
              </div>
            </div>
          </div>

          {/* Trust Signals */}
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-accent-emerald/5 border border-accent-emerald/15 rounded-xl">
              <Icon name="ShieldCheckIcon" size={18} className="text-accent-emerald flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-accent-emerald text-xs font-semibold">30-Day Money Back</p>
                <p className="text-gray-500 text-[11px] mt-0.5">Not satisfied? Full refund, no questions asked.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-white/2 border border-white/5 rounded-xl">
              <Icon name="LockClosedIcon" size={18} className="text-gray-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-gray-300 text-xs font-semibold">256-bit SSL Encryption</p>
                <p className="text-gray-600 text-[11px] mt-0.5">Your data is protected end-to-end.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Secured by */}
      <p className="text-center text-gray-700 text-[10px] font-mono mt-4">
        Secured by Przelewy24 · PCI DSS Compliant
      </p>
    </div>
  );
}
